import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Box, TextField, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Divider, Chip, Autocomplete, MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { blAPI, clientAPI, marcheAPI } from '../services/api';
import { apiError, dateFr, parsePoleNumbers, today } from '../utils/format';

const EMPTY_TRANSPORT = {
  transporteur: '', chauffeur: '', permis_conduire: '', immatriculation: '',
  telephone_chauffeur: '', heure_entree: '', heure_sortie: '',
};

/** Compteur listés / déclarés et doublons pour une saisie de numéros */
function poleStatus(text, declared) {
  const list = parsePoleNumbers(text);
  const seen = new Set();
  const dups = [...new Set(list.filter((n) => (seen.has(n) ? true : (seen.add(n), false))))];
  return { count: list.length, dups, ok: list.length === declared && dups.length === 0 };
}

const PbaDelivery = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);           // fiche client ou saisie libre (texte)
  const [header, setHeader] = useState({ delivery_date: today(), destination: '', commande_ref: '', marche_id: '', adl_id: '' });
  const [choix, setChoix] = useState({ marches: [], adls: [] });       // BC et ADL ouverts du client
  const [transport, setTransport] = useState(EMPTY_TRANSPORT);
  const [lots, setLots] = useState([]);
  const [quantities, setQuantities] = useState({});      // lot id -> quantité
  const [numbers, setNumbers] = useState({});            // type PBA -> texte saisi
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    clientAPI.list().then((r) => setClients(r.data)).catch(() => {});
    loadLots();
  }, []);

  // BC et ADL du client choisi
  const clientId = client && typeof client === 'object' ? client.id : null;
  useEffect(() => {
    setHeader((h) => ({ ...h, marche_id: '', adl_id: '' }));
    if (!clientId) { setChoix({ marches: [], adls: [] }); return; }
    marcheAPI.choix(clientId).then((r) => {
      setChoix(r.data);
      // Un seul BC ouvert : proposé d'office
      if (r.data.marches.length === 1) setHeader((h) => ({ ...h, marche_id: r.data.marches[0].id }));
    }).catch(() => setChoix({ marches: [], adls: [] }));
  }, [clientId]);
  const marcheChoisi = choix.marches.find((m) => m.id === header.marche_id);
  const adlChoisi = choix.adls.find((a) => a.id === header.adl_id);

  const loadLots = async () => {
    try {
      const r = await blAPI.matureLots();
      setLots(r.data);
    } catch (err) {
      setError(apiError(err, 'Erreur lors du chargement des PBA matures'));
    }
  };

  const setQty = (lot, value) => {
    const n = Math.max(0, Math.min(parseInt(value, 10) || 0, lot.quantity_available));
    setQuantities((prev) => ({ ...prev, [lot.id]: n }));
  };

  // Quantités déclarées par type (base du contrôle des numéros)
  const byType = useMemo(() => {
    const acc = {};
    for (const lot of lots) {
      const q = quantities[lot.id] || 0;
      if (q > 0) acc[lot.pba_type] = (acc[lot.pba_type] || 0) + q;
    }
    return acc;
  }, [lots, quantities]);

  const total = Object.values(byType).reduce((s, q) => s + q, 0);
  const clientName = typeof client === 'string' ? client.trim() : client?.nom;

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      const items = Object.entries(quantities)
        .filter(([, q]) => q > 0)
        .map(([id, q]) => ({ maturation_id: Number(id), quantity_delivered: q }));
      const pole_numbers = Object.fromEntries(Object.keys(byType).map((t) => [t, parsePoleNumbers(numbers[t])]));
      const payload = {
        ...header, ...transport, items, pole_numbers,
        ...(client && typeof client === 'object' ? { client_id: client.id } : { client_name: clientName }),
      };
      const r = await blAPI.create(payload);
      navigate(`/bons-livraison/${r.data.delivery.id}`);
    } catch (err) {
      setError(apiError(err, 'Erreur lors de la création du bon de livraison'));
    } finally {
      setLoading(false);
    }
  };

  const field = (state, setState, name, label, props = {}) => (
    <TextField
      fullWidth size="small" label={label} value={state[name]} sx={{ mb: 2 }}
      onChange={(e) => setState((prev) => ({ ...prev, [name]: e.target.value }))}
      {...props}
    />
  );

  return (
    <Layout title="Nouveau bon de livraison">
        <Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Livraison</Typography>
                {field(header, setHeader, 'delivery_date', 'Date de livraison', { type: 'date', InputLabelProps: { shrink: true } })}
                <Autocomplete
                  freeSolo
                  options={clients}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : o.nom)}
                  value={client}
                  onChange={(e, v) => setClient(v)}
                  onInputChange={(e, v, reason) => { if (reason === 'input') setClient(v); }}
                  renderInput={(params) => <TextField {...params} size="small" label="Client *" helperText="Choisir dans la liste (recommandé pour la facturation)" />}
                  sx={{ mb: 2 }}
                />
                {field(header, setHeader, 'destination', 'Destination (ex. Mbour)')}
                {(choix.marches.length > 0 || choix.adls.length > 0) && (
                  <>
                    <TextField select fullWidth size="small" label="Bon de commande client (BC)" sx={{ mb: 2 }} value={header.marche_id}
                      onChange={(e) => setHeader((h) => ({ ...h, marche_id: e.target.value }))}
                      helperText={marcheChoisi ? 'Le BL sera facturé au prix de ce BC' : 'Rattache le BL au marché pour le suivi livré / facturé'}>
                      <MenuItem value="">— Aucun —</MenuItem>
                      {choix.marches.map((m) => <MenuItem key={m.id} value={m.id}>{m.libelle}{m.a_completer ? ' — à compléter' : ''}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth size="small" label="Programme de livraison (ADL)" sx={{ mb: 2 }} value={header.adl_id}
                      onChange={(e) => setHeader((h) => ({ ...h, adl_id: e.target.value }))}>
                      <MenuItem value="">— Sans ADL —</MenuItem>
                      {choix.adls.map((a) => <MenuItem key={a.id} value={a.id}>{a.libelle}</MenuItem>)}
                    </TextField>
                  </>
                )}
                {field(header, setHeader, 'commande_ref', 'Commande / référence', { helperText: marcheChoisi ? `Vide : « BC N° ${marcheChoisi.numero} »` : undefined })}
              </Paper>

              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Transport / chauffeur</Typography>
                {field(transport, setTransport, 'transporteur', 'Transporteur')}
                {field(transport, setTransport, 'chauffeur', 'Chauffeur')}
                {field(transport, setTransport, 'permis_conduire', 'N° permis de conduire')}
                {field(transport, setTransport, 'immatriculation', 'Immatriculation')}
                {field(transport, setTransport, 'telephone_chauffeur', 'Téléphone')}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {field(transport, setTransport, 'heure_entree', 'Entrée usine', { type: 'time', InputLabelProps: { shrink: true } })}
                  {field(transport, setTransport, 'heure_sortie', 'Sortie usine', { type: 'time', InputLabelProps: { shrink: true } })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Le transport peut être complété plus tard, avant la première validation.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>PBA matures disponibles</Typography>
                {lots.length === 0 ? (
                  <Alert severity="info">Aucun PBA mature disponible pour livraison</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type PBA</TableCell>
                          <TableCell>Lot</TableCell>
                          <TableCell>Production</TableCell>
                          <TableCell align="right">Disponible</TableCell>
                          <TableCell align="right" width={130}>À livrer</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lots.map((lot) => (
                          <TableRow key={lot.id} selected={(quantities[lot.id] || 0) > 0}>
                            <TableCell><strong>{lot.pba_type}</strong></TableCell>
                            <TableCell>{lot.reference}</TableCell>
                            <TableCell>{dateFr(lot.production_date)}</TableCell>
                            <TableCell align="right">{lot.quantity_available}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number" size="small" value={quantities[lot.id] || ''}
                                onChange={(e) => setQty(lot, e.target.value)}
                                inputProps={{ min: 0, max: lot.quantity_available, style: { textAlign: 'right' } }}
                                sx={{ width: 100 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              {Object.keys(byType).length > 0 && (marcheChoisi || adlChoisi) && (() => {
                const depassements = Object.entries(byType).flatMap(([t, q]) => [
                  ...(marcheChoisi && marcheChoisi.a_completer ? [`BC ${marcheChoisi.numero} déposé sans saisie : quantités non contrôlées (à compléter dans Marchés clients)`] : []),
                  ...(marcheChoisi && !marcheChoisi.a_completer ? [marcheChoisi.restes[t] === undefined
                    ? `${t} ne figure pas au BC ${marcheChoisi.numero}`
                    : q > marcheChoisi.restes[t] ? `${t} : ${q} à livrer pour ${Math.max(0, marcheChoisi.restes[t])} restant(s) au BC ${marcheChoisi.numero}` : null] : []),
                  ...(adlChoisi && adlChoisi.restes[t] !== undefined && q > adlChoisi.restes[t] ? [`${t} : ${q} à livrer pour ${Math.max(0, adlChoisi.restes[t])} restant(s) à l'${adlChoisi.libelle.split(' (')[0]}`] : []),
                ]).filter((x, k, a) => x && a.indexOf(x) === k);
                return (
                  <Alert severity={depassements.length ? 'warning' : 'success'} sx={{ mb: 3 }}>
                    {depassements.length ? <>Attention :<ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{depassements.map((d) => <li key={d}>{d}</li>)}</ul></>
                      : `Quantités dans le reste à livrer${marcheChoisi ? ` du BC ${marcheChoisi.numero}` : ''}${adlChoisi ? ` et de l'${adlChoisi.libelle.split(' (')[0]}` : ''}.`}
                  </Alert>
                );
              })()}

              {Object.keys(byType).length > 0 && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Numéros des poteaux livrés</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Séparez les numéros par des tirets, virgules, espaces ou retours à la ligne.
                    Le BL ne pourra être validé que si le nombre de numéros correspond à la quantité.
                  </Typography>
                  {Object.entries(byType).map(([type, qty]) => {
                    const st = poleStatus(numbers[type], qty);
                    return (
                      <Box key={type} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1"><strong>{type}</strong> — {qty} déclaré(s)</Typography>
                          <Chip size="small" color={st.ok ? 'success' : 'warning'}
                            label={`${st.count} listé(s)${st.count !== qty ? ` ≠ ${qty}` : ''}`} />
                          {st.dups.length > 0 && <Chip size="small" color="error" label={`Doublon : ${st.dups.join(', ')}`} />}
                        </Box>
                        <TextField
                          fullWidth multiline minRows={2} size="small" value={numbers[type] || ''}
                          placeholder="10661-10662-10660-…"
                          onChange={(e) => setNumbers((prev) => ({ ...prev, [type]: e.target.value }))}
                        />
                      </Box>
                    );
                  })}
                </Paper>
              )}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Total : {total} poteau(x) — {Object.keys(byType).length} désignation(s)</Typography>
                <Button variant="contained" size="large" onClick={handleSubmit}
                  disabled={loading || !clientName || total === 0}>
                  Créer le bon de livraison
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
    </Layout>
  );
};

export default PbaDelivery;
